import React from 'react';

import { PageMeta as Meta } from 'components/Meta';

import { PageMainContentId } from 'constants/Global';

import { useRouter } from 'next/router';
import { sanitizeStaticPath } from 'utils/sanitizeStaticPath';
import { BlogPost, IBlogCategoryPage, PageHero as IPageHero } from 'types';
import BlogPostCardTileList from 'styled/components/BlogPostCardTileList';
import { BaseWithHeaderStylesPortableText } from 'styled/components/portableText';
import { Base, PageHero } from 'styled';
import styled, { useTheme } from 'styled-components';
import { CardsContainer } from 'styled/components/BaseCardTileList/style';
import { PrimaryButton } from '@buoyhealth/common.buoy-button';
import Divider from '@mui/material/Divider';

import useBlogBase from 'hooks/useBlogBase';
import useLanguage from 'hooks/useLanguage';

export interface IBlogHomePageViewProps {
  blogCategories: IBlogCategoryPage[];
  featuredBlogPosts: BlogPost[];
  recentBlogPosts: BlogPost[];
}

const hero: IPageHero = {
  variant: 'general-md',
  title: '',
  description: '',
  color: 'DARK_BLUE',
  desktopImage: {
    id: 'image-28eb38cb90785a750b9ac4add8dadecfe3cd738d-600x566-svg',
    src: 'https://cdn.sanity.io/images/0b678gck/buoy-public-site/28eb38cb90785a750b9ac4add8dadecfe3cd738d-600x566.svg?w=768&q=70&auto=format&dpr=1',
    alt: '',
    crop: null,
    hotspot: null,
    metadata: null,
  },
  mobileImage: {
    id: '',
    src: '',
    alt: '',
    crop: null,
    hotspot: null,
    metadata: null,
  },
  desktopImageWidth: 0,
  mobileImageWidth: 0,
  modules: [],
  buttonLink: '',
  buttonLabel: '',
  buttonVariant: '',
  gtmTrackerEventName: '',
};

const HeroContainer = styled(Base)`
  ${({ theme }) => theme.mediaQueries['max-sm']} {
    .PageHero__text-container--general-md {
      margin-bottom: 0;
    }
  }
  img {
    align-self: flex-end;
    justify-self: flex-end;
    padding-right: 2rem;
    flex-grow: 1;
  }
`;

export const Section = styled(Base)`
  margin-top: 2rem;
  margin-bottom: 2rem;

  ${({ theme }) => theme.mediaQueries.md} {
    &:first-of-type {
      margin-top: 4rem;
      margin-bottom: 0;
    }
  }

  h2 {
    text-align: center;
    margin: 3rem 0;

    ${({ theme }) => theme.mediaQueries['max-sm']} {
      margin-bottom: 3rem;
    }
  }
`;

export const BlogCategoryButton = styled(PrimaryButton)`
  min-height: 100px;
  font-size: 1.5rem;
  border-radius: 1rem;
  text-align: center;
`;

export const Container = styled(Base)`
  ${CardsContainer} {
    margin: 0 0 3rem;
  }

  padding-bottom: 2rem;
`;

const BlogHomePageView: React.FC<IBlogHomePageViewProps> = ({
  blogCategories,
  featuredBlogPosts,
  recentBlogPosts,
}) => {
  const { blogBase } = useBlogBase();
  const Language = useLanguage();
  const theme = useTheme();
  const { asPath: pathname } = useRouter();

  const pageHero = {
    ...hero,
    title: Language.t('BlogHomePage.hero.title'),
    description: Language.t('BlogHomePage.hero.description'),
    desktopImage: {
      ...hero.desktopImage,
      alt: Language.t('BlogHomePage.hero.imgAlt'),
    },
  };

  return (
    <div id={PageMainContentId} className="relative">
      <Meta
        title={Language.t('BlogHomePage.meta.title')}
        description={Language.t('BlogHomePage.meta.description')}
        image={pageHero.desktopImage}
        pathname={sanitizeStaticPath(pathname)}
      />
      <HeroContainer>
        <PageHero hero={pageHero} />
      </HeroContainer>
      <BaseWithHeaderStylesPortableText>
        <Container className="z-2 relative">
          {!!blogCategories.length && (
            <Section className="module-content-padding-x">
              <CardsContainer>
                {blogCategories.map((cat) => {
                  const slugParts = cat.slug.split('/');
                  if (slugParts.length > 2) {
                    // this is a "subcategory", and we don't want it to appear on the home page
                    return;
                  }

                  return (
                    <BlogCategoryButton
                      key={cat.id}
                      href={`${blogBase}${cat.slug}`}
                      withBuoyTheme
                    >
                      {cat.title}
                    </BlogCategoryButton>
                  );
                })}
              </CardsContainer>
              {!featuredBlogPosts.length && (
                <Divider
                  sx={{ margin: { xs: '3rem 2rem', md: '4rem 2rem 0' } }}
                />
              )}
            </Section>
          )}
          {!!featuredBlogPosts.length && (
            <Section
              className="module-content-padding-x"
              backgroundColor={theme.palette.teal[0]}
              pt="1rem"
            >
              <h2>{Language.t('BlogHomePage.featuredPosts')}</h2>
              <BlogPostCardTileList
                blogPosts={featuredBlogPosts}
                blogPostCount={featuredBlogPosts.length}
                slug=""
                page={1}
              />
            </Section>
          )}
          {!!recentBlogPosts.length && (
            <Section className="module-content-padding-x">
              <h2>{Language.t('BlogHomePage.recentPosts')}</h2>
              <BlogPostCardTileList
                blogPosts={recentBlogPosts}
                blogPostCount={recentBlogPosts.length}
                slug=""
                page={1}
              />
            </Section>
          )}
        </Container>
      </BaseWithHeaderStylesPortableText>
    </div>
  );
};

export default BlogHomePageView;
